import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Loader2, X } from 'lucide-react'
import { checkEmailAvailability, provisionEmail } from '../../onboarding/api'
import { assistantNameToEmail } from '../../onboarding/string'

interface AssistantExpandedProps {
  initialName: string
  initialEmail: string | null
}

export default function AssistantExpanded({ initialName, initialEmail }: AssistantExpandedProps) {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')

  const initialLocal = (initialEmail ?? '').split('@')[0] ?? ''

  const [name, setName] = useState(initialName)
  const [emailLocal, setEmailLocal] = useState(initialLocal)
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const nameChanged = name.trim() !== initialName.trim()
  const emailChanged = emailLocal !== initialLocal
  const isDirty = nameChanged || emailChanged
  const canSave = name.trim().length > 0 && emailLocal.length > 0 &&
    (!emailChanged || emailStatus === 'available')

  useEffect(() => {
    if (!emailChanged) {
      setEmailStatus('idle')
      return
    }
    if (!emailLocal) {
      setEmailStatus('idle')
      return
    }

    setEmailStatus('checking')
    const timer = setTimeout(async () => {
      try {
        const result = await checkEmailAvailability(emailLocal)
        setEmailStatus(result.available ? 'available' : 'taken')
      } catch {
        setEmailStatus('idle')
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [emailLocal, emailChanged])

  const handleSave = async () => {
    if (!canSave || !isDirty) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      await provisionEmail(emailLocal, name.trim())
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('assistant.failedToSave'))
    } finally {
      setSaving(false)
    }
  }

  const useSuggested = () => {
    const suggested = assistantNameToEmail(name.trim())
    if (suggested) setEmailLocal(suggested)
  }

  return (
    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
      {error && <p className="text-xs text-rose/70">{error}</p>}

      {/* Name */}
      <div className="space-y-1">
        <label className="text-xs text-pearl/50">{t('assistant.nameLabel')}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
          placeholder={t('assistant.namePlaceholder')}
          maxLength={50}
          disabled={saving}
          className="w-full bg-slate-mid/30 rounded-lg px-3 py-1.5 text-sm text-ivory placeholder:text-pearl/55 outline-none focus:ring-1 focus:ring-gold/40 disabled:opacity-50"
        />
      </div>

      {/* Email */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="text-xs text-pearl/50">{t('assistant.emailLabel')}</label>
          {nameChanged && assistantNameToEmail(name.trim()) !== emailLocal && (
            <button
              type="button"
              onClick={useSuggested}
              className="text-xs text-gold/70 hover:text-gold transition-colors"
            >
              {t('assistant.useSuggested')}
            </button>
          )}
        </div>
        <div className="flex items-center bg-slate-mid/30 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-gold/40">
          <input
            type="text"
            value={emailLocal}
            onChange={(e) => setEmailLocal(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave() }}
            placeholder={t('assistant.emailLocalPlaceholder')}
            maxLength={50}
            disabled={saving}
            className="flex-1 bg-transparent px-3 py-1.5 text-sm text-ivory placeholder:text-pearl/55 outline-none disabled:opacity-50"
          />
          <span className="text-pearl/55 text-sm pr-3 shrink-0">@tend.bot</span>
        </div>

        {/* Availability feedback (only shown when email has changed) */}
        {emailChanged && (
          <div className="mt-1.5 ml-1">
            {emailStatus === 'checking' && (
              <div className="flex items-center gap-1.5">
                <Loader2 size={11} className="text-pearl/60 animate-spin" />
                <span className="text-pearl/60 text-xs">{t('assistant.checking')}</span>
              </div>
            )}
            {emailStatus === 'available' && (
              <div className="flex items-center gap-1.5">
                <Check size={11} className="text-emerald" />
                <span className="text-emerald text-xs">{t('assistant.available')}</span>
              </div>
            )}
            {emailStatus === 'taken' && (
              <div className="flex items-center gap-1.5">
                <X size={11} className="text-amber" />
                <span className="text-amber text-xs">{t('assistant.taken')}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-pearl/50">{t('assistant.description')}</p>

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={!isDirty || !canSave || saving}
        className="w-full py-2 rounded-full bg-gold/10 text-gold text-sm font-medium hover:bg-gold/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {saving ? (
          <><Loader2 size={14} className="animate-spin" /> {tc('actions.saving')}</>
        ) : saved ? (
          <><Check size={14} /> {tc('actions.saved')}</>
        ) : (
          tc('actions.save')
        )}
      </button>
    </div>
  )
}
